import ReactDOM from 'react-dom';
import { RefreshCw } from 'lucide-react';
import { setDbManagerTabs } from '../../store/tabsStore';
import SqlQueryEditor from '../ui/SqlQueryEditor';
import ResizableSplitter from '../ui/ResizableSplitter';
import { MAX_RENDER_ROWS, EMPTY_PENDING_ROWS, EMPTY_PENDING_EDITS, EMPTY_PENDING_DELETES } from '../dbGridModel';
import ResultsGrid from '../DbResultsGrid';
import TransposedGrid from '../DbTransposedGrid';
import { EmptyState } from '../dbGridCells';
import DbNoKeyNote from './DbNoKeyNote';
import Tooltip from '../ui/Tooltip';
import DbCopyMenu from '../DbCopyMenu';
import EditableNumberPicker from '../ui/EditableNumberPicker';
import { selectedRowIndexes } from './dbExportSelection';
import { engineOf, normalizeSqlText, PAGE_SIZE_OPTIONS } from './dbResultsTabHelpers';

/** The results tab markup: the SQL editor layout for a query tab, the filter bar + grid for a table tab. A plain function called during render (no hooks), so the element tree is the same as when this markup sat in DbManagerResultsTab. */
export function renderDbResultsTab({
  tab,
  result,
  pageSize,
  isQueryTab,
  queryMaxRows,
  lastDurationMs,
  filterDraft,
  sqlDraft,
  setSqlDraft,
  error,
  failedSql,
  writeError,
  loading,
  loadMoreRows,
  pendingRows,
  pendingEdits,
  pendingDeletes,
  saving,
  primaryKeyCols,
  editingCell,
  editingValue,
  setEditingValue,
  tableColumns,
  colSuggestions,
  setColSuggestions,
  columnTypeMap,
  enumByColumn,
  sortState,
  allLoaded,
  loadingMore,
  totalRows,
  selectedRows,
  selectedCells,
  toggleRowSelected,
  transposed,
  resultSets,
  resultSetIndex,
  setExportOpen,
  setColumnPickerOpen,
  setExportRows,
  setHistoryOpen,
  runningQueryId,
  scrollRestore,
  sqlEditorApiRef,
  filterInputRef,
  copiedFormat,
  copyResult,
  selectResultSet,
  querySchema,
  addPendingRow,
  updatePendingCell,
  removePendingRow,
  cancelEditCell,
  toggleRecordViewWhileEditing,
  commitEditCell,
  stageCellNull,
  openDatePicker,
  handleCellOpen,
  fkByColumn,
  navigateFk,
  columnWidths,
  transposedWidths,
  hiddenColumns,
  hiddenLive,
  formatDraft,
  stopRunningQuery,
  handleColumnResize,
  handleTransposedResize,
  pendingEditCount,
  pendingDeleteCount,
  hasUnsavedGridChanges,
  rememberScrollTop,
  refreshRows,
  saveChanges,
  discardChanges,
  deleteSelectedRows,
  deleteGestureCount,
  deleteGestureAllMarked,
  openContextMenu,
  openHeaderContextMenu,
  openBackgroundContextMenu,
  findMatchKeys,
  activeFindKey,
  toggleCellSelected,
  gridCursorKey,
  commitFilter,
  commitSql,
  commitScript,
  handleSortColumn,
  elapsedLabel,
  stopTitle,
  maxRowsPicker,
  handlePageSizeChange,
  overlays,
  handleFilterChange,
  insertColumnSuggestion,
  handleFilterKeyDown,
  findBarNode,
}) {
  if (isQueryTab) {
    // No output yet → the editor owns the whole tab. Once a run starts, the
    // RequestBuilder-style splitter appears: editor on top, results below.
    const hasQueryOutput = Boolean(loading || error || result);
    const editorPane = (
      <div className="dbm-sql-pane">
        <div className="dbm-sql-bar">
          <button
            type="button"
            className="dbm-pager-btn"
            onClick={formatDraft}
            disabled={!sqlDraft.trim()}
            title="Reformat this SQL (Shift+Alt+F)"
          >
            Format
          </button>
          <button
            type="button"
            className="dbm-pager-btn"
            onClick={() => setHistoryOpen(true)}
            title="Statements run before (Ctrl+Shift+H)"
          >
            History
          </button>
          {/* Until the first result brings the footer, the cap sits here. */}
          {!hasQueryOutput && maxRowsPicker}
        </div>
        <SqlQueryEditor
          value={sqlDraft}
          onChange={(v) => {
            const next = normalizeSqlText(v);
            setSqlDraft(next);
            // Commit per keystroke (request tabs do the same): the workspace
            // snapshot mirror reads tab.sql, so a workspace switch or quit
            // mid-typing persists the draft, not the last run/saved text.
            setDbManagerTabs((prev) =>
              prev.map((t) => (t.id === tab.id && t.sql !== next ? { ...t, sql: next } : t))
            );
          }}
          onRun={commitSql}
          onRunScript={commitScript}
          // Drives the monarch grammar: MySQL needs `#` treated as a line
          // comment, Postgres needs it NOT to be.
          engine={engineOf(tab)}
          schema={querySchema}
          apiRef={sqlEditorApiRef}
          height="100%"
          placeholder="Cmd/Ctrl+Enter runs the statement at the cursor (or the selection). Cmd/Ctrl+Shift+Enter runs everything."
        />
      </div>
    );
    return (
      <div className="dbm-tab">
        {hasQueryOutput ? (
          <ResizableSplitter
            direction="vertical"
            initialSplit={38}
            minSize={0}
            maxSize={100}
            minFirstPanelPx={140}
            minSecondPanelPx={100}
            className="dbm-query-split"
          >
            {editorPane}
            <div className="dbm-results-pane">
              {findBarNode}
              {!loading && !error && resultSets && resultSets.length > 1 && (
                // Multi-statement scripts: one switcher pill per result set,
                // row-less statements labeled by their affected-row count.
                <div style={{ display: 'flex', gap: 4, padding: '6px 8px 2px', flexWrap: 'wrap', flexShrink: 0, alignItems: 'center' }}>
                  {resultSets.map((s, i) => {
                    const hasCols = s?.columns?.length > 0;
                    // Zero columns + zero affected is ambiguous (a zero-row
                    // SELECT inside a multi-statement script, where the
                    // describe fallback can't run) — don't call it "affected".
                    const label = hasCols
                      ? `Result ${i + 1} (${s.rows.length.toLocaleString()} row${s.rows.length === 1 ? '' : 's'})`
                      : Number(s?.affectedRows ?? 0) > 0
                        ? `${Number(s.affectedRows).toLocaleString()} affected`
                        : 'No rows';
                    const active = i === resultSetIndex;
                    return (
                      <button
                        key={i}
                        type="button"
                        className="dbm-pager-btn"
                        style={active ? { borderColor: 'var(--accent)', color: 'var(--accent)' } : undefined}
                        onClick={() => selectResultSet(i)}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
              {!loading && !error && result && !(result.columns?.length > 0) ? (
                // Row-less statement (INSERT/UPDATE/SET/…): a status line
                // beats the old "Query returned no columns". Zero affected +
                // zero columns can also be a zero-row SELECT in a
                // multi-statement script (no describe fallback there), so
                // that case reads neutrally instead of claiming DML ran.
                <EmptyState>
                  {Number(result.affectedRows ?? 0) > 0
                    ? `${Number(result.affectedRows).toLocaleString()} row${Number(result.affectedRows) === 1 ? '' : 's'} affected`
                    : 'Statement completed — no rows returned'}
                </EmptyState>
              ) : (
                transposed && !loading && !error && result && selectedRows.size > 0 ? (
                  // The record view on query results too (read-only), like table tabs.
                  <TransposedGrid
                    result={result}
                    selectedRows={selectedRows}
                    pendingEdits={EMPTY_PENDING_EDITS}
                    pendingDeletes={EMPTY_PENDING_DELETES}
                    transposedWidths={transposedWidths}
                    onTransposedResize={handleTransposedResize}
                    editable={false}
                    editingCell={null}
                    editingValue=""
                    onCellOpen={handleCellOpen}
                    onCellContextMenu={openContextMenu}
                    pendingRows={EMPTY_PENDING_ROWS}
                    selectedCells={selectedCells}
                    onCellSelect={toggleCellSelected}
                    findMatchKeys={findMatchKeys}
                    activeFindKey={activeFindKey}
                    cursorKey={gridCursorKey}
                  />
                ) : (
                <ResultsGrid
                  loading={loading}
                  elapsedLabel={elapsedLabel}
                  onStop={runningQueryId ? stopRunningQuery : undefined}
                  stopTitle={stopTitle}
                  error={error}
                  failedSql={failedSql}
                  connectionName={tab?.dbName || null}
                  result={result}
                  canAddRow={false}
                  editable={false}
                  pendingRows={EMPTY_PENDING_ROWS}
                  pendingEdits={EMPTY_PENDING_EDITS}
                  editingCell={null}
                  editingValue=""
                  onCellOpen={handleCellOpen}
                  onCellContextMenu={openContextMenu}
                  selectedCells={selectedCells}
                  onCellSelect={toggleCellSelected}
                  rowSelectable
                  selectedRows={selectedRows}
                  onToggleRowSelect={toggleRowSelected}
                  findMatchKeys={findMatchKeys}
                  activeFindKey={activeFindKey}
                  cursorKey={gridCursorKey}
                  hiddenColumns={hiddenColumns}
                  onHeaderContextMenu={openHeaderContextMenu}
                />
                )
              )}
              {!loading && !error && result?.rows?.length > 0 && (
                <div className="dbm-footer">
                  <span className="dbm-footer-count">
                    {result.rows.length.toLocaleString()} row{result.rows.length === 1 ? '' : 's'}
                    {result.truncated && (
                      <span
                        className="dbm-footer-truncated"
                        title={`The backend stopped collecting at ${queryMaxRows.toLocaleString()} rows so a runaway query cannot exhaust memory. Raise the limit beside this count, add a LIMIT to the SQL, or use Export to write the whole result to a file.`}
                      >
                        {' '}— truncated
                      </span>
                    )}
                    {result.rows.length >= MAX_RENDER_ROWS && (
                      // Table browse has said this for a while; the query tab
                      // never did, and it matters more here now that the cap is
                      // raisable — fetching 200,000 rows the grid will not draw
                      // would otherwise look like it simply worked.
                      <span
                        className="dbm-footer-truncated"
                        title={`The grid draws at most ${MAX_RENDER_ROWS.toLocaleString()} rows. Use Export for the whole result.`}
                      >
                        {' '}— display limit
                      </span>
                    )}
                  </span>
                  <div className="dbm-pager">
                    {lastDurationMs != null && (
                      <span className="dbm-pager-range" title="Time the last run took">
                        {lastDurationMs < 1000
                          ? `${lastDurationMs} ms`
                          : `${(lastDurationMs / 1000).toFixed(2)} s`}
                      </span>
                    )}
                    {/* The cap that will apply to the NEXT run. It was always
                        being enforced at 10,000 by the backend; it was simply
                        invisible, so a truncated result looked like the whole
                        answer. */}
                    {maxRowsPicker}
                    <DbCopyMenu onCopy={copyResult} copiedFormat={copiedFormat} />
                    <button
                      type="button"
                      className="dbm-pager-btn"
                      data-keeps-selection
                      onClick={() => { setExportRows(selectedRowIndexes(selectedRows, selectedCells)); setExportOpen(true); }}
                    >
                      Export…
                    </button>
                  </div>
                </div>
              )}
            </div>
          </ResizableSplitter>
        ) : (
          editorPane
        )}
        {overlays}
      </div>
    );
  }

  return (
    <div className="dbm-tab">
      {(
        <div className="dbm-toolbar">
          <div className="dbm-input-group">
            <span className="dbm-toolbar-label dbm-input-group-label">WHERE</span>
            <input
              type="text"
              ref={filterInputRef}
              className="dbm-filter-input"
              value={filterDraft}
              onChange={handleFilterChange}
              onKeyDown={handleFilterKeyDown}
              onBlur={() => setColSuggestions(null)}
              placeholder="e.g. id > 100 AND status = 'active'"
            />
          </div>
          {/* The app's Tooltip, not the native title (owner, 30.09.2026). The
              span carries the hover: a disabled button gets none, and its
              "why" is exactly what the tooltip is for then. */}
          <Tooltip content="Save or discard pending changes first" side="bottom" disabled={!hasUnsavedGridChanges}>
            <span className="dbm-tip-wrap">
              <button type="button" className="dbm-run-btn" onClick={commitFilter} disabled={hasUnsavedGridChanges || loading}>Run</button>
            </span>
          </Tooltip>
          <Tooltip content={hasUnsavedGridChanges ? 'Save or discard pending changes first' : 'Refresh (Ctrl+R)'} side="bottom">
            <span className="dbm-tip-wrap">
              <button type="button" className="dbm-icon-btn" onClick={refreshRows} disabled={hasUnsavedGridChanges || loading} aria-label="Refresh">
                <RefreshCw size={14} />
              </button>
            </span>
          </Tooltip>
          {/* Export/Import moved to the grid's right-click context menu. */}
        </div>
      )}
      {colSuggestions && ReactDOM.createPortal(
        <div
          className="dyn-var-suggestions"
          style={{ left: colSuggestions.x, top: colSuggestions.y }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {colSuggestions.items.map((col, i) => (
            <div
              key={col.name}
              className={`dyn-var-suggestion-item${i === colSuggestions.active ? ' dyn-var-suggestion-item--active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                insertColumnSuggestion(col);
              }}
              onMouseEnter={() => setColSuggestions((prev) => (prev ? { ...prev, active: i } : null))}
            >
              <span className="dyn-var-suggestion-name">{col.name}</span>
              {col.type && <span className="dyn-var-suggestion-desc">{col.type}</span>}
            </div>
          ))}
        </div>,
        document.body
      )}
      {findBarNode}
      <DbNoKeyNote show={!!tab?.tableName && tableColumns.length > 0 && primaryKeyCols.length === 0} />
      <div className="dbm-grid-row">
      {transposed && !loading && !error && result && selectedRows.size > 0 ? (
        <TransposedGrid
          result={result}
          selectedRows={selectedRows}
          pendingEdits={pendingEdits}
          pendingDeletes={pendingDeletes}
          columnTypes={columnTypeMap}
          enumByColumn={isQueryTab ? undefined : enumByColumn}
          fkByColumn={fkByColumn}
          onFkNavigate={navigateFk}
          transposedWidths={transposedWidths}
          onTransposedResize={handleTransposedResize}
          editable={!isQueryTab && !loading && !error}
          editingCell={editingCell}
          editingValue={editingValue}
          onCellOpen={handleCellOpen}
          onCellContextMenu={openContextMenu}
          onEditingValueChange={setEditingValue}
          onCommitEdit={commitEditCell}
          onCancelEdit={cancelEditCell}
          onEditTab={toggleRecordViewWhileEditing}
          onEditNull={() => editingCell && stageCellNull(editingCell.rowIndex, editingCell.colIndex)}
          pendingRows={pendingRows}
          onUpdatePendingCell={updatePendingCell}
          onRemovePendingRow={removePendingRow}
          selectedCells={selectedCells}
          onCellSelect={toggleCellSelected}
          onDatePick={isQueryTab ? undefined : openDatePicker}
          anchorToSelection={scrollRestore.anchor}
          findMatchKeys={findMatchKeys}
          activeFindKey={activeFindKey}
          cursorKey={gridCursorKey}
        />
      ) : (
        <ResultsGrid
          loading={loading}
          elapsedLabel={elapsedLabel}
          onStop={runningQueryId ? stopRunningQuery : undefined}
          stopTitle={stopTitle}
          error={error}
          failedSql={failedSql}
          connectionName={tab?.dbName || null}
          result={result}
          canAddRow={!isQueryTab && !loading && !error}
          editable={!isQueryTab && !loading && !error}
          pendingRows={pendingRows}
          pendingEdits={pendingEdits}
          pendingDeletes={pendingDeletes}
          editingCell={editingCell}
          editingValue={editingValue}
          onCellOpen={handleCellOpen}
          onCellContextMenu={openContextMenu}
          onBackgroundContextMenu={isQueryTab ? undefined : openBackgroundContextMenu}
          onEditingValueChange={setEditingValue}
          onCommitEdit={commitEditCell}
          onCancelEdit={cancelEditCell}
          onEditTab={toggleRecordViewWhileEditing}
          onEditNull={() => editingCell && stageCellNull(editingCell.rowIndex, editingCell.colIndex)}
          onAddRow={addPendingRow}
          onUpdatePendingCell={updatePendingCell}
          onRemovePendingRow={removePendingRow}
          sortable={!isQueryTab && !loading && !error}
          sortState={sortState}
          onSortColumn={handleSortColumn}
          selectedRows={selectedRows}
          onToggleRowSelect={toggleRowSelected}
          selectedCells={selectedCells}
          onCellSelect={toggleCellSelected}
          onDatePick={isQueryTab ? undefined : openDatePicker}
          columnTypes={isQueryTab ? undefined : columnTypeMap}
          enumByColumn={isQueryTab ? undefined : enumByColumn}
          fkByColumn={isQueryTab ? undefined : fkByColumn}
          onFkNavigate={isQueryTab ? undefined : navigateFk}
          columnWidths={columnWidths}
          onColumnResize={handleColumnResize}
          findMatchKeys={findMatchKeys}
          activeFindKey={activeFindKey}
          cursorKey={gridCursorKey}
          hiddenColumns={hiddenColumns}
          onHeaderContextMenu={openHeaderContextMenu}
          onScrollNearEnd={isQueryTab || allLoaded ? undefined : loadMoreRows}
          loadingMore={loadingMore}
          onScrollPosition={rememberScrollTop}
          initialScrollTop={scrollRestore.top}
          scrollRestoreKey={scrollRestore.key}
          anchorToSelection={scrollRestore.anchor}
        />
      )}
      </div>
      {!isQueryTab && writeError && (
        <div className="dbm-write-error">
          <span className="dbm-write-error-label">Save failed</span>
          <span className="dbm-write-error-msg">{writeError}</span>
        </div>
      )}
      {!isQueryTab && !error && result && (
        <div className="dbm-footer">
          <div className="dbm-footer-left">
            <span className="dbm-footer-count">
              {totalRows != null
                ? `${totalRows.toLocaleString()} row${totalRows === 1 ? '' : 's'}${(tab.filterText || '').trim() ? ' (filtered)' : ''}`
                : ''}
              {hiddenLive.length > 0 && (
                // A hidden column is invisible by definition, and one someone
                // forgot they hid reads as missing data. Say so, and make the
                // label the way back.
                <button
                  type="button"
                  className="dbm-footer-hidden"
                  onClick={() => setColumnPickerOpen(true)}
                  title={`Hidden: ${hiddenLive.join(', ')}`}
                >
                  {hiddenLive.length} column{hiddenLive.length === 1 ? '' : 's'} hidden
                </button>
              )}
            </span>
            {result?.rows?.length > 0 && (
              <>
                <DbCopyMenu onCopy={copyResult} copiedFormat={copiedFormat} />
              </>
            )}
            {/* Marks rather than deletes, so the label says so — and says
                which way the toggle will go, since the same button un-marks a
                selection that is already fully marked. Shown for a cell
                selection too: Delete acts on the rows those cells sit in, and
                a button that vanished when you clicked a cell would look like
                the action had gone away. */}
            {deleteGestureCount > 0 && (
              <button
                type="button"
                className="dbm-delete-btn"
                onClick={deleteSelectedRows}
                disabled={saving}
                title="Mark for deletion — Save commits it (Delete)"
              >
                {deleteGestureAllMarked ? 'Keep' : 'Delete'} {deleteGestureCount} row
                {deleteGestureCount === 1 ? '' : 's'}
              </button>
            )}
            {hasUnsavedGridChanges && (
              <>
                <button type="button" className="dbm-pager-btn" onClick={discardChanges} disabled={saving} title="Discard pending changes (Esc)">
                  Cancel
                </button>
                <button
                  type="button"
                  className="dbm-pager-btn"
                  style={{ background: 'var(--accent)', borderColor: 'var(--accent)', color: 'var(--accent-fg, #fff)' }}
                  onClick={saveChanges}
                  disabled={saving}
                  title="Save pending changes (⌘S / Ctrl+S)"
                >
                  {saving
                    ? 'Saving…'
                    : `Save (${pendingEditCount + pendingRows.length + pendingDeleteCount})`}
                </button>
              </>
            )}
          </div>
          <div className="dbm-pager">
            <EditableNumberPicker
              className="dbm-page-size"
              value={pageSize}
              onChange={handlePageSizeChange}
              presets={PAGE_SIZE_OPTIONS}
              min={1}
              max={MAX_RENDER_ROWS}
              suffix=" / load"
              disabled={hasUnsavedGridChanges || loading}
              menuMinWidth={130}
              ariaLabel="Rows loaded per fetch"
            />
            {result.rows.length > 0 && (
              <span className="dbm-pager-range">
                {loadingMore
                  ? `Loading… ${result.rows.length.toLocaleString()} loaded`
                  : totalRows != null && result.rows.length < totalRows
                    ? `${result.rows.length.toLocaleString()} of ${totalRows.toLocaleString()} loaded`
                    : `${result.rows.length.toLocaleString()} loaded`}
                {result.rows.length >= MAX_RENDER_ROWS && (
                  // The grid stops drawing past this, so loading further would
                  // spend memory on rows nobody can see. Export gets the rest.
                  <span
                    className="dbm-footer-truncated"
                    title={`The grid draws at most ${MAX_RENDER_ROWS.toLocaleString()} rows. Use Export for the whole table, or narrow it with WHERE.`}
                  >
                    {' '}— display limit
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      )}
      {overlays}
    </div>
  );
}
